/** STRUCTURA FAȚADEI — pagina (`/p/:projectId/fatada-structura/:sol`, `sol` =
 *  arhitectura): vântul pe anvelopă după CR 1-1-4/2012 și dimensionarea
 *  peretelui cortină, așa cum le socotește `architecture/fatada_structura/`.
 *
 *  Pagina e un CITITOR: nu ține nicio stare pe server. Opțiunile (categoria de
 *  teren, q_b, altitudinea, norma de săgeată, schema statică, aria elementului)
 *  sunt parametrii cererii, iar raportul se reface la fiecare schimbare — un
 *  proiectant de fațade poate întoarce pe toate părțile ipotezele și vede pe loc
 *  ce schimbă fiecare. Categoria de teren vine, implicit, din harta orașului
 *  (roza sectoarelor); omul o poate impune.
 *
 *  Se arată UN corp o dată (pastile + tabelul de comparație): șapte corpuri cu
 *  patru elevații fiecare făceau o pagină de nederulat (Cătălin, 26.10.2026).
 *
 *  Separată de Studio-ul structurii: codul structurii nu știe de ea, ea nu
 *  știe de el; se ajunge aici printr-un buton, atât. */
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";
import { getStructuraFatada, type FataVant, type OptiuniStructuraFatada, type StructuraFatada } from "../api";
import { useT } from "../i18n";
import ObsHost from "../structure/ObsHost";
import type { Cheie } from "../i18n";
import { cadruPlan, culoarePresiune, culoareTeren, culoareText, kpa, laAzimut, maximulCorpului, panaSvg, rezumatCorp,
         scaraFata } from "./desen";
import "./fatadaStructura.css";

type Corp = StructuraFatada["corpuri"][number];
const TERENURI = ["0", "I", "II", "III", "IV"] as const;
type Arata = "succ" | "pres" | "succ1" | "pres1";
const ARATA: Arata[] = ["succ", "pres", "succ1", "pres1"];
const CHEIE_ARATA: Record<Arata, Cheie> = { succ: "fs.suctiune", pres: "fs.presiune", succ1: "fs.suctiune_1", pres1: "fs.presiune_1" };

const valoare = (c: { w_pres: number; w_succ: number; w_pres_1m2: number; w_succ_1m2: number }, ce: Arata): number =>
  ce === "succ" ? c.w_succ : ce === "pres" ? c.w_pres : ce === "succ1" ? c.w_succ_1m2 : c.w_pres_1m2;

const n1 = (x: number | null | undefined, z = 1) => (x == null ? "—" : x.toFixed(z));
const pct = (f: number) => (f * 100).toFixed(f * 100 < 10 ? 1 : 0);

export default function StructuraFatada() {
  const { projectId = "", sol = "" } = useParams();
  const t = useT();
  const [optiuni, setOptiuni] = useState<OptiuniStructuraFatada>({ norma: "np102", schema: "simpla", arie: 1 });
  const [qbText, setQbText] = useState("");
  const [altText, setAltText] = useState("");
  const [raport, setRaport] = useState<StructuraFatada | null>(null);
  const [eroare, setEroare] = useState<string | null>(null);
  const [lucrez, setLucrez] = useState(false);
  const [arata, setArata] = useState<Arata>("succ");
  const [corpAles, setCorpAles] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId || !sol) return;
    let viu = true;
    setLucrez(true); setEroare(null);
    getStructuraFatada(projectId, sol, optiuni)
      .then((r) => { if (viu) setRaport(r); })
      .catch((e: { message?: string }) => { if (viu) setEroare(e?.message ?? String(e)); })
      .finally(() => { if (viu) setLucrez(false); });
    return () => { viu = false; };
  }, [projectId, sol, optiuni]);

  const pune = (p: Partial<OptiuniStructuraFatada>) => setOptiuni((o) => ({ ...o, ...p }));
  const amp = raport?.amplasament;
  const corpuri = raport?.corpuri ?? [];
  const corp = corpuri.find((c) => c.id === corpAles) ?? corpuri[0] ?? null;
  const auto = amp?.teren_auto ?? null;

  // paginile din Shell își aduc singure containerul derulant (`ob-main`), ca
  // «Costuri»: `main`-ul din Shell e `overflow: hidden`, iar fără el pagina
  // lungă se reteza la înălțimea ferestrei
  return (
    <ObsHost className="ob-main">
    <div className="fs-pagina">
      <header className="fs-cap-pagina">
        <Link className="btn mic" to={`/p/${projectId}/structura`}>{t("fs.inapoi")}</Link>
        <h1>{t("fs.titlu")}</h1>
        <p className="nota">{t("fs.subtitlu")}</p>
      </header>

      <section className="card">
        <div className="fs-card-cap"><h2>{t("fs.amplasament")}</h2></div>
        <div className="fs-optiuni">
          <label>{t("fs.teren")}
            <select className="inp" value={optiuni.teren ?? ""} onChange={(e) => pune({ teren: e.target.value || undefined })}>
              <option value="">
                {auto ? t("fs.teren.auto_rezultat", { cat: auto.categorie })
                  : amp && !optiuni.teren ? t("fs.teren.auto_lipsa", { cat: amp.teren }) : t("fs.teren.auto")}
              </option>
              {TERENURI.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {amp && <span className="nota">{t("fs.teren_cat", { cat: amp.teren, descriere: amp.teren_descriere, z0: amp.z0_m })} · {amp.teren_sursa}
              {amp.teren_pe_directii && <> · {t("fs.teren.pe_directii")}</>}</span>}
          </label>
          <label>{t("fs.q_b")}
            <input className="inp" inputMode="decimal" placeholder={amp ? String(amp.q_b_kpa) : ""} value={qbText}
                   onChange={(e) => setQbText(e.target.value)}
                   onBlur={() => { const v = parseFloat(qbText.replace(",", ".")); pune({ q_b: Number.isFinite(v) ? v : null }); }} />
            {amp && <span className="nota">{amp.q_b_kpa} kPa · {amp.q_b_sursa}</span>}
          </label>
          <label>{t("fs.altitudine")}
            <input className="inp" inputMode="numeric" value={altText} onChange={(e) => setAltText(e.target.value)}
                   onBlur={() => { const v = parseFloat(altText); pune({ altitudine: Number.isFinite(v) ? v : null }); }} />
            {amp && <span className="nota">{t("fs.q_b_calcul")}: {amp.q_b_calcul_kpa} kPa (× {amp.factor_altitudine})</span>}
          </label>
          <label>{t("fs.norma")}
            <select className="inp" value={optiuni.norma} onChange={(e) => pune({ norma: e.target.value as "np102" | "en13830" })}>
              <option value="np102">{t("fs.norma.np102")}</option>
              <option value="en13830">{t("fs.norma.en13830")}</option>
            </select>
          </label>
          <label>{t("fs.schema")}
            <select className="inp" value={optiuni.schema} onChange={(e) => pune({ schema: e.target.value as "simpla" | "continua_2" })}>
              <option value="simpla">{t("fs.schema.simpla")}</option>
              <option value="continua_2">{t("fs.schema.continua_2")}</option>
            </select>
          </label>
          <label>{t("fs.arie")}
            <select className="inp" value={String(optiuni.arie ?? 1)} onChange={(e) => pune({ arie: Number(e.target.value) })}>
              {[0.5, 1, 2, 4, 10].map((a) => <option key={a} value={a}>{a} m²</option>)}
            </select>
          </label>
        </div>
        {amp && (
          <div className="fs-fapte">
            <span>{t("fs.localitate")}: <b>{amp.localitate ?? t("fs.necunoscuta")}{amp.judet ? `, ${amp.judet}` : ""}</b></span>
            <span>{t("fs.q_b")}: <b>{amp.q_b_kpa} kPa</b> <span className="nota">({amp.q_b_sursa})</span></span>
          </div>
        )}
        {!raport && lucrez && <div className="fs-asteapta">{t("fs.se_incarca")}</div>}
        {eroare && <p className="fs-eroare" style={{ marginTop: 10 }}>{t("fs.eroare", { motiv: eroare })}</p>}
        {raport && (
          <div className={`fs-doua${lucrez ? " fs-refac" : ""}`}>
            <RozaTeren raport={raport} corp={corp} />
            <GraficQp raport={raport} />
          </div>
        )}
      </section>

      {raport && corpuri.length > 0 && (
        <section className={`card${lucrez ? " fs-refac" : ""}`}>
          <div className="fs-card-cap"><h2>{t("fs.corpuri")}</h2><p className="nota">{t("fs.alege_corp")}</p></div>
          <div className="seg fs-chips">
            {corpuri.map((c) => (
              <button key={c.id} type="button" className={corp?.id === c.id ? "on" : undefined} onClick={() => setCorpAles(c.id)}>{c.cod}</button>
            ))}
          </div>
          <div className="fs-tabel-loc">
          <table className="fs-tabel">
            <thead><tr>
              <th>{t("fs.col.corp")}</th><th>{t("fs.col.h")}</th><th>{t("fs.col.fete")}</th><th>{t("fs.col.etaje")}</th>
              <th>{t("fs.col.wp")}</th><th>{t("fs.col.ws")}</th><th>{t("fs.col.fmax")}</th><th>{t("fs.col.profil_max")}</th><th>{t("fs.col.verdict")}</th>
            </tr></thead>
            <tbody>
              {corpuri.map((c) => {
                const r = rezumatCorp(c);
                return (
                  <tr key={c.id} className={`alegibil${corp?.id === c.id ? " ales" : ""}`} onClick={() => setCorpAles(c.id)}>
                    <td>{c.cod}</td><td>{n1(c.h_m)}</td><td>{c.presiuni.fete.length}</td><td>{c.niveluri.length}</td>
                    <td>{kpa(r.wPres1)}</td><td>{kpa(r.wSucc1)}</td><td>{n1(r.fMax, 0)}</td>
                    <td>{r.areCortina ? (r.profil ?? "—") : t("fs.fara_cortina_scurt")}</td>
                    <td className={r.areCortina ? (r.trece ? "bine" : "rau") : undefined}>{r.areCortina ? (r.trece ? t("fs.trece") : t("fs.nu_trece")) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </section>
      )}

      {corp && <CorpDetaliu corp={corp} arata={arata} setArata={setArata} lucrez={lucrez} />}

      {raport && (
        <section className="card">
          <div className="fs-doua-text">
            <div className="fs-panou">
              <h3>{t("fs.avertismente")}{raport.avertismente.length ? ` (${raport.avertismente.length})` : ""}</h3>
              {raport.avertismente.length
                ? <div className="fs-avert-cutie"><ul className="fs-avert">{raport.avertismente.map((a, i) => <li key={i}>{a}</li>)}</ul></div>
                : <p className="nota">{t("fs.fara_avertismente")}</p>}
            </div>
            <div className="fs-panou">
              <h3>{t("fs.ipoteze")}</h3>
              <ul className="fs-avert">
                {(raport.ipoteze.norme ?? []).map((x) => <li key={x}>{x}</li>)}
                <li>c_pi = {JSON.stringify(raport.ipoteze.cpi)} · c_s·c_d = {String(raport.ipoteze.cs_cd)} · γ_Q = {String(raport.ipoteze.gamma_q)}</li>
                <li>Al: {JSON.stringify(raport.ipoteze.aluminiu)} · {String(raport.ipoteze.sistem)}, {String(raport.ipoteze.clasa_vant_sistem_kpa)} kPa</li>
              </ul>
              <h3>{t("fs.nefacute")}</h3>
              <ul className="fs-avert">{(raport.ipoteze.nefacute ?? []).map((x) => <li key={x}>{x}</li>)}</ul>
            </div>
          </div>
        </section>
      )}
    </div>
    </ObsHost>
  );
}

/** Un corp: planul cu direcțiile, forțele pe fețe, elevațiile colorate și
 *  montanții cortinei. */
function CorpDetaliu({ corp: c, arata, setArata, lucrez }: { corp: Corp; arata: Arata; setArata: (a: Arata) => void; lucrez: boolean }) {
  const t = useT();
  const wMax = maximulCorpului(c.presiuni.fete);
  return (
    <section className={`card fs-corp${lucrez ? " fs-refac" : ""}`}>
      <div className="fs-card-cap">
        <h2>{t("fs.corp", { cod: c.cod })}</h2>
        <p className="nota">{t("fs.corp_rezumat", { h: n1(c.h_m), n: c.presiuni.fete.length, etaje: c.niveluri.length })}</p>
      </div>
      <div className="fs-plan-si-directii">
        <div className="fs-panou fs-plan"><Plan corp={c} /></div>
        <div className="fs-panou">
          <h3>{t("fs.directii")}</h3>
          <ul className="fs-avert">
            {c.presiuni.directii.map((d) => (
              <li key={d.id}>{t("fs.dir_rand", { id: d.id, dinspre: d.dinspre, unghi: d.unghi_deg, teren: d.teren, b: n1(d.b), d: n1(d.d), hd: n1(d.h_pe_d, 2), e: n1(d.e) })}</li>
            ))}
          </ul>
          <h3 style={{ marginTop: 18 }}>{t("fs.forte_titlu")}</h3>
          <div className="fs-tabel-loc">
          <table className="fs-tabel">
            <thead><tr><th>{t("fs.fata")}</th>{c.presiuni.directii.map((d) => <th key={d.id}>{d.id} ({d.dinspre}, {d.teren})</th>)}<th>max +</th><th>max −</th></tr></thead>
            <tbody>
              {c.presiuni.fete.map((f) => (
                <tr key={f.id}><td>{f.nume}{f.cortina.length ? " ▣" : ""}</td>
                  {c.presiuni.directii.map((d) => <td key={d.id}>{n1(f.F_kn[d.id], 0)}</td>)}
                  <td>{n1(f.F_pres_kn, 0)}</td><td>{n1(f.F_succ_kn, 0)}</td></tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      <div className="fs-comutator">
        <span className="lbl">{t("fs.arata")}</span>
        <span className="seg">
          {ARATA.map((a) => (
            <button key={a} type="button" className={arata === a ? "on" : undefined} onClick={() => setArata(a)}>{t(CHEIE_ARATA[a])}</button>
          ))}
        </span>
        <span className="fs-legenda">
          <span>0</span>
          <span className="bara" style={{ background: `linear-gradient(90deg, ${culoarePresiune(arata.startsWith("succ") ? -0.001 : 0.001, 1)}, ${culoarePresiune(arata.startsWith("succ") ? -1 : 1, 1)})` }} />
          <span>{n1(wMax, 2)} {t("fs.legenda_kpa")}</span>
        </span>
      </div>
      {/* FEȚELE LATE câte una pe rând; cele ÎNGUSTE (capetele unei bare, de
          câțiva metri) una lângă alta — una sub alta lăsau câte o fâșie
          desenată într-un card gol cât pagina */}
      <div className="fs-elevatii">
        {fetePeRanduri(c).map(({ fete, inguste }) => (
          <div key={fete.map((f) => f.id).join("|")} className={`fs-elevatii-rand${inguste ? " inguste" : ""}`}>
            {fete.map((f) => <Elevatie key={f.id} fata={f} h={c.h_m} ridicare={c.ridicare_m} arata={arata} wMax={wMax} ingusta={inguste} />)}
          </div>
        ))}
      </div>

      <div className="fs-sub">
      <h3>{t("fs.cortina")}</h3>
      {!c.are_cortina && <p className="nota">{t("fs.fara_cortina")}</p>}
      {c.compunere?.dimensionare && (
        <p className="nota fs-compunere">
          {t("fs.compunere", { montant: c.compunere.profil?.montant ?? "—", traversa: c.compunere.profil?.traversa ?? "—",
                               teren: c.compunere.dimensionare.teren, qb: c.compunere.dimensionare.q_b_kpa, w: n1(c.compunere.dimensionare.w_k_max_kpa, 2) })}
          {c.compunere.dimensionare.lipsa?.length ? <> · <span className="rau">{t("fs.compunere_lipsa", { n: c.compunere.dimensionare.lipsa.length })}</span></> : null}
        </p>
      )}
      {c.are_cortina && !c.compunere?.dimensionare && <p className="nota">{t("fs.compunere_veche")}</p>}
      {c.cortina.portiuni.map((p) => (
        <div key={p.op + p.face} className="fs-portiune">
          <p className="nota"><b>{t("fs.portiune", { op: p.op, fata: p.fata, u0: n1(p.u0), u1: n1(p.u1), modul: n1(p.modul_mediu_m, 2) })}</b></p>
          <div className="fs-tabel-loc">
          <table className="fs-tabel">
            <thead><tr>
              <th>{t("fs.col.nivel")}</th><th>{t("fs.col.L")}</th><th>{t("fs.col.n")}</th><th>{t("fs.col.w")}</th>
              <th>{t("fs.col.q")}</th><th>{t("fs.col.ix")}</th><th>{t("fs.col.profil")}</th><th>{t("fs.col.sageata")}</th>
              <th>{t("fs.col.sigma")}</th><th>{t("fs.col.rd")}</th><th>{t("fs.col.g")}</th><th>{t("fs.col.traversa")}</th><th>{t("fs.col.verdict")}</th>
            </tr></thead>
            <tbody>
              {[...p.niveluri].reverse().map((nv) => {
                const g = nv.guvernant;
                const rau = !g?.profil || !nv.traversa?.profil;
                return (
                  <tr key={nv.level} className={rau ? "rau" : undefined} title={g ? t("fs.guvernant", { u: n1(g.u, 2), zona: g.zona ?? "—", caz: g.caz ?? "—" }) : undefined}>
                    <td>{nv.level}</td><td>{n1(nv.L_m, 2)}</td><td>{nv.n_montanti}</td><td>{n1(nv.w_k_max_kpa, 2)}</td>
                    <td>{n1(nv.q_k_max_kn_m, 2)}</td><td>{n1(g?.Ix_nec_cm4, 0)}</td><td>{g?.profil ?? "—"}</td>
                    <td>{n1(g?.sageata_mm, 1)} / {n1(g?.sageata_adm_mm, 1)}</td>
                    <td>{n1(g?.sigma_mpa, 0)} / {n1(g?.f_d_mpa, 0)}</td><td>{n1(nv.R_d_max_kn, 2)}</td><td>{n1(nv.G_k_max_kn, 2)}</td>
                    <td>{nv.traversa?.profil ?? "—"}</td>
                    <td>{g?.profil && nv.traversa?.profil ? t("fs.trece") : (g?.verdict ?? nv.traversa?.verdict ?? "—")}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      ))}
      {c.are_cortina && (
        <p className="nota" style={{ marginTop: 14 }}>{t("fs.profile")}: {Object.entries(c.cortina.profile_montanti).map(([k, v]) => `${k} × ${v}`).join(" · ")}
          {c.cortina.catalog.estimat && <><br />{t("fs.catalog_estimat")}</>}</p>
      )}
      </div>
    </section>
  );
}

/** Roza terenului: cele opt sectoare de azimut colorate după categoria care
 *  iese din harta orașului, cu direcțiile vântului corpului ales pe margine.
 *  Când omul a impus o categorie, sectoarele rămân, înmuiate: spun ce a
 *  văzut harta, nu ce se socotește. */
function RozaTeren({ raport, corp }: { raport: StructuraFatada; corp: Corp | null }) {
  const t = useT();
  const amp = raport.amplasament;
  const auto = amp.teren_auto;
  const S = 300, cx = S / 2, cy = S / 2, R = 118;
  return (
    <div className="fs-panou fs-roza">
      <p className="nota">{t("fs.roza_titlu", { raza: auto ? Math.round(auto.raza_m) : 800 })}</p>
      <svg viewBox={`0 0 ${S} ${S}`} width={S} height={S} role="img">
        {(auto?.sectoare ?? []).map((s) => {
          const [lx, ly] = laAzimut(cx, cy, R * 0.68, (s.az0 + 22.5) % 360);
          return (
            <g key={s.k}>
              <path d={panaSvg(cx, cy, R, s.az0, s.az0 + 45)} fill={culoareTeren(s.categorie)} stroke="#fff" strokeWidth={1.5}
                    opacity={amp.teren_pe_directii ? 1 : 0.55}>
                <title>{t("fs.roza_sector", { nume: s.nume, az0: s.az0, az1: s.az1, cat: s.categorie, inalt: pct(s.inalt),
                                               construit: pct(s.construit), c500: pct(s.construit_500), apa: pct(s.apa), verde: pct(s.verde), n: s.n_cladiri })}</title>
              </path>
              <text x={lx} y={ly - 3} fontSize={10} textAnchor="middle" fill="#1d1f22">{s.nume}</text>
              <text x={lx} y={ly + 9} fontSize={11} fontWeight={700} textAnchor="middle" fill="#1d1f22">{s.categorie}</text>
            </g>
          );
        })}
        {!auto && <circle cx={cx} cy={cy} r={R} fill="#fff" stroke="#c9ccd2" strokeDasharray="4 3" />}
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="#1d1f22" strokeWidth={0.8} />
        <text x={cx} y={cy - R - 6} fontSize={10} textAnchor="middle" fill="#6b6f76">N</text>
        {corp && corp.presiuni.directii.map((d) => {
          const az = d.unghi_deg;
          const [x0, y0] = laAzimut(cx, cy, R + 24, az);
          const [x1, y1] = laAzimut(cx, cy, R + 4, az);
          const [tx, ty] = laAzimut(cx, cy, R + 33, az);
          return (
            <g key={d.id}>
              <line x1={x0} y1={y0} x2={x1} y2={y1} stroke="#b42318" strokeWidth={2} markerEnd="url(#fs-sageata-roza)" />
              <text x={tx} y={ty + 3} fontSize={9} textAnchor="middle" fill="#b42318">{d.id}</text>
            </g>
          );
        })}
        <circle cx={cx} cy={cy} r={3} fill="#1d1f22" />
        <defs><marker id="fs-sageata-roza" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 z" fill="#b42318" /></marker></defs>
      </svg>
      <div className="legenda-teren">
        {TERENURI.map((c) => <span key={c} style={{ "--c": culoareTeren(c) } as CSSProperties}>{c}</span>)}
        <span className="nota" style={{ flexBasis: "100%" }}>{t("fs.legenda_teren")}</span>
      </div>
      {auto ? (
        <p className="nota">{t("fs.roza_cladiri", { n: auto.n_cladiri, raza: Math.round(auto.raza_m), sursa: auto.sursa })}
          {!auto.complet && <> · {t("fs.roza_incompleta", { gata: auto.dale?.gata ?? 0, total: auto.dale?.total ?? 0 })}</>}</p>
      ) : <p className="nota">{t("fs.roza_lipsa")}</p>}
    </div>
  );
}

/** q_p(z) pe cele cinci categorii, cu cea aleasă îngroșată și plăcile etajelor punctate. */
function GraficQp({ raport }: { raport: StructuraFatada }) {
  const t = useT();
  const W = 620, H = 300, m = { l: 44, r: 16, t: 12, b: 28 };
  const profil = raport.profil_q_p;
  const cat = raport.amplasament.teren;
  const zMax = Math.max(...Object.values(profil).flatMap((l) => l.map((p) => p[0])), 1);
  const qMax = Math.max(...Object.values(profil).flatMap((l) => l.map((p) => p[1])), 0.1);
  const X = (q: number) => m.l + (q / qMax) * (W - m.l - m.r);
  const Y = (z: number) => H - m.b - (z / zMax) * (H - m.t - m.b);
  const corp = raport.corpuri[0];
  return (
    <div className="fs-panou fs-grafic">
      <p className="nota">{t("fs.grafic_qp")}</p>
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img">
        {[0.25, 0.5, 0.75, 1].map((f) => (
          <g key={f}>
            <line x1={X(qMax * f)} y1={m.t} x2={X(qMax * f)} y2={H - m.b} stroke="#e4e6ea" />
            <text x={X(qMax * f)} y={H - 10} fontSize={10} textAnchor="middle" fill="#6b6f76">{(qMax * f).toFixed(2)} kPa</text>
          </g>
        ))}
        {[0.25, 0.5, 0.75, 1].map((f) => (
          <g key={f}>
            <line x1={m.l} y1={Y(zMax * f)} x2={W - m.r} y2={Y(zMax * f)} stroke="#f0f1f3" />
            <text x={m.l - 6} y={Y(zMax * f) + 3} fontSize={10} textAnchor="end" fill="#6b6f76">{Math.round(zMax * f)} m</text>
          </g>
        ))}
        {Object.entries(profil).map(([c, pts]) => (
          <polyline key={c} fill="none" stroke={c === cat ? "#1d1f22" : "#b9bcc3"} strokeWidth={c === cat ? 2.2 : 1}
                    points={pts.map(([z, q]) => `${X(q)},${Y(z)}`).join(" ")} />
        ))}
        {Object.entries(profil).map(([c, pts]) => {
          const p = pts[pts.length - 1];
          return <text key={c} x={X(p[1]) + 3} y={Y(p[0]) - 2} fontSize={10} fill={c === cat ? "#1d1f22" : "#8a8d94"}>{c}</text>;
        })}
        {corp && profil[cat] && corp.niveluri.map((n) => (
          <circle key={n.level} cx={X(n.q_p_kpa)} cy={Y(n.z1 + corp.ridicare_m)} r={3} fill="#1d1f22" />
        ))}
      </svg>
    </div>
  );
}

/** Planul corpului: fețele (cele cu cortină îngroșate), numele lor și săgețile celor patru direcții. */
function Plan({ corp }: { corp: Corp }) {
  const t = useT();
  const cadru = useMemo(() => cadruPlan(corp.poly, 300, 52), [corp.poly]);
  const cx = corp.poly.reduce((s, p) => s + p[0], 0) / corp.poly.length;
  const cy = corp.poly.reduce((s, p) => s + p[1], 0) / corp.poly.length;
  const R = Math.max(...corp.poly.map((p) => Math.hypot(p[0] - cx, p[1] - cy)));
  return (
    <div>
      <h3>{t("fs.plan")}</h3>
      <svg viewBox={`0 0 ${cadru.w} ${cadru.h}`} width={cadru.w} height={cadru.h} role="img">
        <polygon points={corp.poly.map((p) => cadru.la(p[0], p[1]).join(",")).join(" ")} fill="#f3f1ec" stroke="#8a8d94" />
        {corp.presiuni.fete.map((f) => {
          const [x0, y0] = cadru.la(f.a[0], f.a[1]), [x1, y1] = cadru.la(f.b[0], f.b[1]);
          const mx = (f.a[0] + f.b[0]) / 2 + f.n[0] * 1.6, my = (f.a[1] + f.b[1]) / 2 + f.n[1] * 1.6;
          const [tx, ty] = cadru.la(mx, my);
          return (
            <g key={f.id}>
              <line x1={x0} y1={y0} x2={x1} y2={y1} stroke={f.cortina.length ? "#1d6fa6" : "#1d1f22"} strokeWidth={f.cortina.length ? 4 : 2} />
              <text x={tx} y={ty + 3} fontSize={9} textAnchor="middle" fill="#1d1f22">{f.nume}</text>
            </g>
          );
        })}
        {corp.presiuni.directii.map((d) => {
          const s = cadru.la(cx - d.w[0] * (R + 7), cy - d.w[1] * (R + 7));
          const e = cadru.la(cx - d.w[0] * (R + 1.5), cy - d.w[1] * (R + 1.5));
          const l = cadru.la(cx - d.w[0] * (R + 10.5), cy - d.w[1] * (R + 10.5));
          return (
            <g key={d.id}>
              <line x1={s[0]} y1={s[1]} x2={e[0]} y2={e[1]} stroke="#b42318" strokeWidth={2} markerEnd="url(#fs-sageata)" />
              <text x={l[0]} y={l[1] + 3} fontSize={9} textAnchor="middle" fill="#b42318">{d.id} {d.dinspre} · {d.teren}</text>
            </g>
          );
        })}
        <defs><marker id="fs-sageata" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 z" fill="#b42318" /></marker></defs>
      </svg>
    </div>
  );
}

/** Lățimea maximă a desenului unei elevații și înălțimea lui, în px. */
const ELEVATIE_W_PX = 1400, ELEVATIE_H_PX = 300;
/** Sub atâta lățime desenată, o elevație stă lângă celelalte înguste. */
const ELEVATIE_INGUSTA_PX = 560;
const latimeElevatie = (f: FataVant, h: number) => scaraFata(f.lungime_m, h, ELEVATIE_W_PX, ELEVATIE_H_PX).w;

/** Fețele corpului pe rânduri: fiecare față lată singură, în ordinea ei; cele
 *  înguste toate împreună, pe un rând la sfârșit (se rup singure pe ecran). */
function fetePeRanduri(c: Corp): { fete: FataVant[]; inguste: boolean }[] {
  const late = c.presiuni.fete.filter((f) => latimeElevatie(f, c.h_m) >= ELEVATIE_INGUSTA_PX);
  const inguste = c.presiuni.fete.filter((f) => latimeElevatie(f, c.h_m) < ELEVATIE_INGUSTA_PX);
  return [...late.map((f) => ({ fete: [f], inguste: false })),
          ...(inguste.length ? [{ fete: inguste, inguste: true }] : [])];
}

/** Elevația unei fețe: celulele colorate după presiunea aleasă, hotarele
 *  zonelor (linii punctate), planșeele, porțiunile de cortină (bară albastră). */
function Elevatie({ fata, h, ridicare, arata, wMax, ingusta = false }:
                  { fata: FataVant; h: number; ridicare: number; arata: Arata; wMax: number; ingusta?: boolean }) {
  const t = useT();
  const sc = scaraFata(fata.lungime_m, h, ELEVATIE_W_PX, ELEVATIE_H_PX);
  const Y = (z: number) => sc.h - (z + ridicare) * sc.px;
  const taieturi = [...new Set(Object.values(fata.directii).flatMap((d) => d.taieturi_u))];
  const ce = t(CHEIE_ARATA[arata]);
  return (
    <div className="fs-panou fs-elevatie" style={ingusta ? { width: Math.max(360, sc.w + 40) } : undefined}>
      <p className="titlu">{t("fs.elevatie", { nume: fata.nume, L: n1(fata.lungime_m), ce, p: n1(fata.w_pres_1m2_max, 2), s: n1(fata.w_succ_1m2_max, 2) })}
        <span className="nota">{t("fs.forte_anvelopa", { p: n1(fata.F_pres_kn, 0), s: n1(fata.F_succ_kn, 0) })}</span></p>
      <svg viewBox={`0 0 ${sc.w + 2} ${sc.h + 14}`} width={sc.w + 2} height={sc.h + 14} role="img">
        {fata.niveluri.map((n) => n.celule.map((c) => {
          const w = valoare(c, arata);
          const x = c.u0 * sc.px, lat = (c.u1 - c.u0) * sc.px, y = Y(n.z1), ina = n.h * sc.px;
          return (
            <g key={`${n.level}-${c.u0}`}>
              <rect x={x} y={y} width={lat} height={ina} fill={culoarePresiune(w, wMax)} stroke="#fff" strokeWidth={0.4}>
                <title>{`${fata.nume} etaj ${n.level} u ${c.u0}–${c.u1} m (${c.arie_m2} m²): ${kpa(w)} kPa`}</title>
              </rect>
              {lat >= 34 && ina >= 13 && (
                <text x={x + lat / 2} y={y + ina / 2 + 3} fontSize={9} textAnchor="middle" fill={culoareText(w, wMax)}>{kpa(w)}</text>
              )}
            </g>
          );
        }))}
        {taieturi.map((u) => <line key={u} x1={u * sc.px} y1={0} x2={u * sc.px} y2={sc.h} stroke="#1d1f22" strokeDasharray="3 3" strokeWidth={0.8} />)}
        {fata.cortina.length > 0 && <rect x={0} y={sc.h + 4} width={sc.w} height={4} fill="#1d6fa6" />}
        <rect x={0} y={0} width={sc.w} height={sc.h} fill="none" stroke="#1d1f22" strokeWidth={0.8} />
      </svg>
    </div>
  );
}
